import React, { useEffect, useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    Button,
    IconButton,
    Tooltip,
    Alert
} from '@mui/material';
import { collection, getDocs, getFirestore, Timestamp } from 'firebase/firestore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { AuthService, UserData } from '../services/auth';
import { useNavigate } from 'react-router-dom';

const USERS_COLLECTION = 'cruiseclick_users';

interface FirestoreUser extends Omit<UserData, 'createdAt'> {
    id: string;
    createdAt: Timestamp;
}

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<FirestoreUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const checkAdminAndFetchUsers = async () => {
            try {
                const currentUser = AuthService.getCurrentUser();
                if (!currentUser) {
                    navigate('/login');
                    return;
                }

                const adminStatus = await AuthService.isAdmin(currentUser.uid);
                if (!adminStatus) {
                    setError('Access denied. Admin privileges required.');
                    return;
                }

                setIsAdmin(true);
                const db = getFirestore();
                const usersCollection = collection(db, USERS_COLLECTION);
                const userSnapshot = await getDocs(usersCollection);
                
                const userList = userSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as FirestoreUser[];

                setUsers(userList);
            } catch (err) {
                setError('Failed to fetch users');
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };

        checkAdminAndFetchUsers();
    }, [navigate]);

    const handleMakeAdmin = async (userId: string) => {
        try {
            await AuthService.setAdminRole(userId);
            // Refresh the user list
            const updatedUsers = users.map(user => {
                if (user.id === userId) {
                    return { ...user, role: 'admin' as const };
                }
                return user;
            });
            setUsers(updatedUsers);
        } catch (err) {
            console.error('Error making user admin:', err);
            setError('Failed to update user role');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 4 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!isAdmin) {
        return (
            <Container>
                <Alert severity="warning" sx={{ mt: 4 }}>
                    You need administrator privileges to access this page.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
                User Management
            </Typography>
            
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User ID</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>First Name</TableCell>
                            <TableCell>Last Name</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>{user.id}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{user.firstName}</TableCell>
                                <TableCell>{user.lastName}</TableCell>
                                <TableCell>{user.role}</TableCell>
                                <TableCell>
                                    {new Date(user.createdAt.seconds * 1000).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                    {user.role !== 'admin' && (
                                        <Tooltip title="Make Admin">
                                            <IconButton
                                                onClick={() => handleMakeAdmin(user.id)}
                                                color="primary"
                                                size="small"
                                            >
                                                <AdminPanelSettingsIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    );
};

export default UserManagement; 